import { useState } from 'react'
import { Button, Toolbar } from 'react-md'
import { _makeSignedProxyUrl } from 'state/finance-viilutalous-actions'
import LoadingState from 'ui/components/LoadingState'
import ViiluDialog from 'ui/components/ViiluDialog'
import PDFPreview from 'ui/components/PDFPreview'
import { useSelectedFinanceCompany } from 'ui/ManagerFinance/hooks/useSelectedFinanceCompany'
import { useIntl } from 'react-intl'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import ReportFormInput from './ReportFormInput'
import { generateQueryParams } from 'util/queryUtils'
import type { ReportFormData } from './types'
import DropdownField from 'ui/components/DropdownField'

type ReportDialogProps = {
	title: string
	formData: ReportFormData
	onHide: () => void
}

const ReportDialog = ({ title, formData, onHide }: ReportDialogProps) => {
	const intl = useIntl()

	const { type, preferredFormat, supportedFormats, formInputs } = formData

	const formatOptions = supportedFormats.map((format) => {
		let label = format.toUpperCase()
		if (format === 'pdf') {
			label = intl.formatMessage({
				defaultMessage: 'PDF',
				description: 'PDF format',
			})
		} else if (format === 'xls') {
			label = intl.formatMessage({
				defaultMessage: 'Excel',
				description: 'Excel format',
			})
		}
		return { label, value: format }
	})

	const { company, loading: loadingCompany } = useSelectedFinanceCompany()
	const [loadingPdf, setLoadingPdf] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [pdfUrl, setPdfUrl] = useState<string | null>(null)

	const defaultValues = {
		format: preferredFormat,
	}
	formInputs.forEach((field) => {
		defaultValues[field.key] = field.defaultValue ? field.defaultValue() : ''
	})
	const methods = useForm({ defaultValues })
	const loading = loadingCompany || loadingPdf
	const format = methods.watch('format')

	const handleGenerateReport = async (data) => {
		if (!company || !company.uuid) {
			setError(
				intl.formatMessage({
					defaultMessage: 'Yritystä ei ole valittu.',
					description: 'Error when no company is selected',
				}),
			)
			return
		}
		if (!company.integrations?.viiluTalous?.databaseSource) {
			setError(
				intl.formatMessage({
					defaultMessage: 'Viilu Talous ei ole integroitu tähän yritykseen.',
					description: 'Error when Viilu Talous is not integrated',
				}),
			)
			return
		}
		setLoadingPdf(true)
		try {
			const response = await _makeSignedProxyUrl({
				companyUUID: company.uuid,
				databaseSource: company.integrations?.viiluTalous?.databaseSource,
				type,
				resourceId: format ?? 'pdf',
			})
			if (!response?.ok) {
				throw new Error('Invalid response from server')
			}
			const url = await response.text()
			if (format === 'pdf') {
				setPdfUrl(`${url}${generateQueryParams(data)}`)
			} else {
				window.open(`${url}${generateQueryParams(data)}`, '_blank')
			}
		} catch (e) {
			console.error(e)
			setError(
				intl.formatMessage({
					defaultMessage:
						'Raportin luonti epäonnistui. Yritä myöhemmin uudelleen.',
					description: 'Error when report generation fails',
				}),
			)
		} finally {
			setLoadingPdf(false)
		}
	}

	const renderForm = () => (
		<FormProvider {...methods}>
			<form
				onSubmit={methods.handleSubmit(handleGenerateReport)}
				style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
			>
				<Controller
					name={'format'}
					control={methods.control}
					render={({ field: { onChange, value } }) => (
						<DropdownField
							key={'format'}
							label={intl.formatMessage({
								defaultMessage: 'Tiedostomuoto',
								description: 'File format',
							})}
							itemLabel={'label'}
							itemValue={'value'}
							options={formatOptions}
							hasSelectAll={false}
							nullSelectAll={false}
							value={value}
							_onOptionSelect={(value) => {
								onChange(value)
							}}
						/>
					)}
				/>
				{formInputs.map((input) => (
					<ReportFormInput key={input.key} input={input} />
				))}
				<Button style={{ marginTop: '20px' }} raised secondary type="submit">
					{intl.formatMessage({
						defaultMessage: 'Luo raportti',
						description: 'Button to generate report',
					})}
				</Button>
			</form>
		</FormProvider>
	)

	let content
	if (error) {
		content = (
			<div>
				<p style={{ color: 'red' }}>{error}</p>
				<Button
					style={{ marginTop: '10px' }}
					raised
					secondary
					onClick={() => {
						setError(null)
						setPdfUrl(null)
						setLoadingPdf(false)
					}}
				>
					{intl.formatMessage({
						defaultMessage: 'Yritä uudelleen',
						description: 'Button to retry after error',
					})}
				</Button>
			</div>
		)
	} else if (pdfUrl) {
		content = <PDFPreview fileURL={pdfUrl} refreshing={false} />
	} else if (loading) {
		content = (
			<div className="full-width full-height flex-center">
				<LoadingState />
			</div>
		)
	} else {
		content = renderForm()
	}

	return (
		<ViiluDialog
			aria-label={title}
			id="responsive-dialog"
			dialogClassName="responsive-dialog"
			contentClassName="responsive-dialog-content"
			dialogStyle={{
				display: 'flex',
				flexDirection: 'column',
			}}
			visible
			onHide={onHide}
			focusOnMount={false}
			toolbarStyle={{
				background: 'var(--color-secondary-dark)',
			}}
		>
			<Toolbar
				colored
				title={title}
				style={{
					background: 'var(--color-secondary-dark)',
					position: 'fixed',
					width: '100%',
					zIndex: 2,
				}}
				nav={
					<Button icon onClick={onHide}>
						close
					</Button>
				}
			/>
			<section
				className="md-toolbar-relative"
				style={{ padding: '1rem', height: '90vh' }}
			>
				{content}
			</section>
		</ViiluDialog>
	)
}

export default ReportDialog
